import React, { useEffect, useState, useCallback, useRef } from "react";
import "./CommunitiesFilter.scss";

const CommunitiesFilter = ({
  open,
  privacy,
  sortKey,
  sorts = [],
  onChangePrivacy,
  onChangeSortKey,
  onClear,
  onClose,
}) => {
  /* state */
  const [isClosing, setIsClosing] = useState(false);
  const firstFocusableRef = useRef(null);

  /* default sort by creation date */
  const pickDefaultSortKey = useCallback(() => {
    if (!Array.isArray(sorts) || sorts.length === 0) return null;

    const byKeyPriority = [
      "createdAt_desc",
      "createdAt",
      "created_desc",
      "created",
      "date_desc",
      "date",
    ];
    for (const k of byKeyPriority) {
      const hit = sorts.find((s) => String(s.key).toLowerCase() === k);
      if (hit) return hit.key;
    }

    const byLabel = sorts.find((s) => {
      const lbl = String(s.label || "").toLowerCase();
      return (
        lbl.includes("дата") ||
        lbl.includes("створ") ||
        lbl.includes("date") ||
        lbl.includes("created")
      );
    });
    if (byLabel) return byLabel.key;

    return sorts[0].key;
  }, [sorts]);

  /* init sortKey if empty */
  useEffect(() => {
    if (!sortKey) {
      const def = pickDefaultSortKey();
      if (def && typeof onChangeSortKey === "function") onChangeSortKey(def);
    }
  }, [sortKey, pickDefaultSortKey, onChangeSortKey]);

  /* lock body scroll while open */
  useEffect(() => {
    if (!open) return;
    const body = document.body;
    const prev = body.style.overflow;
    body.style.overflow = "hidden";
    return () => {
      body.style.overflow = prev || "";
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      setIsClosing(false);
    }
  }, [open]);

  /* ESC + focus first chip */
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => firstFocusableRef.current?.focus(), 10);
    const onKey = (e) => {
      if (e.key === "Escape") handleRequestClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(t);
      window.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /* close with fade-out sync */
  const handleRequestClose = useCallback(() => {
    if (isClosing) return;
    if (!open) return;

    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose?.();
    }, 240);
  }, [isClosing, open, onClose]);

  const stop = (e) => e.stopPropagation();

  /* overlay classes */
  const overlayClasses = [
    "communities-filter__overlay",
    open && !isClosing ? "is-open" : "",
    isClosing ? "is-closing" : "",
  ]
    .join(" ")
    .trim();

  if (!open && !isClosing) {
    return null;
  }

  return (
    <div
      className={overlayClasses}
      onClick={handleRequestClose}
      role="dialog"
      aria-modal="true"
      aria-label="Фільтри спільнот"
      aria-hidden={!open && !isClosing}
    >
      <div
        className={`communities-filter__sheet ${open ? "is-open" : ""}`}
        onClick={stop}
        role="document"
      >
        <header className="sheet__header">
          <h4>Фільтри</h4>
          <button
            className="sheet__close"
            onClick={handleRequestClose}
            aria-label="Закрити"
            type="button"
          >
            ✕
          </button>
        </header>

        <div className="sheet__section">
          <div className="sheet__label">Доступ</div>
          <div className="chip-row" role="group" aria-label="Фільтр доступу">
            <button
              ref={firstFocusableRef}
              className={`chip ${privacy === "any" ? "is-active" : ""}`}
              onClick={() => onChangePrivacy?.("any")}
              type="button"
            >
              Усі
            </button>
            <button
              className={`chip ${privacy === "open" ? "is-active" : ""}`}
              onClick={() => onChangePrivacy?.("open")}
              type="button"
            >
              Відкриті
            </button>
            <button
              className={`chip ${privacy === "closed" ? "is-active" : ""}`}
              onClick={() => onChangePrivacy?.("closed")}
              type="button"
            >
              Закриті
            </button>
          </div>
        </div>

        <div className="sheet__section">
          <div className="sheet__label">Сортування</div>
          <div className="radio-col" role="radiogroup" aria-label="Сортування">
            {sorts.map((s) => (
              <label key={s.key} className="radio">
                <input
                  type="radio"
                  name="sortKey"
                  value={s.key}
                  checked={sortKey === s.key}
                  onChange={() => onChangeSortKey?.(s.key)}
                />
                <span>{s.label}</span>
              </label>
            ))}
          </div>
        </div>

        <footer className="sheet__footer">
          <button className="btn btn--primary" onClick={onClear} type="button">
            Очистити
          </button>
        </footer>
      </div>
    </div>
  );
};

export default CommunitiesFilter;
